/* eslint-disable no-undef */
import { searchByImage } from 'shared/api/imageSearch';
import { ImagePrompt } from 'shared/components/ImageSearch/constants';
import { aiResultsStore, safeParseAIOutput } from './aiResultsStore';

export const processImageWithAI = async (imageSrc: string | null, builtInOnly?: boolean) => {
  try {
    if (!imageSrc) {
      throw new Error('No image provided');
    }

    if (typeof LanguageModel !== 'undefined' && typeof LanguageModel.create === 'function') {
      const builtInAvailable = await LanguageModel.availability({
        expectedInputs: [{ type: 'image' }, { type: 'text' }],
      });

      if (builtInAvailable === 'available' || builtInAvailable === 'downloadable') {
        localStorage.setItem('imageToProcess', imageSrc);
        const session = await LanguageModel.create({
          expectedInputs: [{ type: 'image' }],
        });

        const img = new Image();
        img.src = imageSrc;

        const builtInOutput = await session.prompt([
          {
            role: 'user',
            content: [
              { type: 'text', value: ImagePrompt },
              { type: 'image', value: img },
            ],
          },
        ]);

        const safeResult = safeParseAIOutput(builtInOutput);
        aiResultsStore.updateResult(safeResult);
        // Store in localStorage
        try {
          let existingSuggestions = JSON.parse(localStorage.getItem('suggestions') || '[]');
          existingSuggestions = [safeResult];
          localStorage.setItem('suggestions', JSON.stringify(existingSuggestions));
        } catch (e) {
          console.error('Failed to update localStorage:', e);
        }
        return {
          result: safeResult,
        };
      }
    }
    if (builtInOnly) {
      throw new Error('LanguageModel API not available');
    }
    // Fallback if LanguageModel is not available or not 'available'
    const base64Image = imageSrc.replace(/^data:image\/(png|jpeg|jpg);base64,/, '');
    try {
      const products = await searchByImage(base64Image, '10', '1', 'v1', 'png');
      return {
        products: products,
      };
    } catch (error) {
      console.error('Image Search API Error:', error);
      return {
        products: [],
      };
    }
  } catch (error: any) {
    console.error('AI Error:', error);
    throw new Error(`AI processing error: ${error.message}`);
  }
};

export const drawBoundingBoxes = (imageSrc: string | null, detections: any) => {
  return new Promise<string | null>((resolve) => {
    if (!imageSrc || !detections || detections.length === 0) {
      resolve(null);
      return;
    }

    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(null);
        return;
      }
      ctx.drawImage(img, 0, 0);

      detections.forEach((detection: any) => {
        if (detection.confidence > 0.85) {
          ctx.beginPath();
          ctx.arc(
            detection.bounding_box.x + detection.bounding_box.width / 2,
            detection.bounding_box.y + detection.bounding_box.height / 2,
            Math.max(detection.bounding_box.width, detection.bounding_box.height) / 2,
            0,
            2 * Math.PI
          );
          ctx.strokeStyle = 'red';
          ctx.lineWidth = 3;
          ctx.stroke();
        }
      });

      resolve(canvas.toDataURL());
    };
    img.src = imageSrc;
  });
};

export const compressImage = (imageSrc: string): Promise<string> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');

      // Drastically reduce size to fit in localStorage
      const maxDimension = 400; // Much smaller max dimension
      let width = img.width;
      let height = img.height;

      if (width > height) {
        if (width > maxDimension) {
          height = Math.round((height * maxDimension) / width);
          width = maxDimension;
        }
      } else {
        if (height > maxDimension) {
          width = Math.round((width * maxDimension) / height);
          height = maxDimension;
        }
      }

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0, width, height);
        // Use PNG format as requested
        resolve(canvas.toDataURL('image/png'));
      } else {
        resolve(imageSrc);
      }
    };
    img.src = imageSrc;
  });
};

export const buildImageSearchUrl = (baseUrl: string, params: Record<string, any>): string => {
  // Clean 'null' from q and other search terms
  if (params.q) {
    params.q = params.q
      .split(' ')
      .filter((word: string) => word && word !== 'null')
      .join(' ')
      .trim();
  }

  // If q is empty after cleaning, return just the base URL
  if (!params.q) {
    return baseUrl;
  }

  // Remove params with null, undefined, or empty string values
  Object.keys(params).forEach((key) => {
    if (
      params[key] === null ||
      params[key] === undefined ||
      params[key] === '' ||
      params[key] === 'null'
    ) {
      delete params[key];
    }
  });

  // Build query string
  const queryString = new URLSearchParams(params).toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};
